<div class="driver-management">
  <div class="page-header">
    <div>
      <h1>Drivers</h1>
      <p class="text-muted">Manage local driver users and link them to hotspot passenger profiles when they already exist in customer data.</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-primary" type="button" (click)="startCreate()">
        <i class="material-icons">person_add</i>
        New Driver
      </button>
    </div>
  </div>

  <div class="save-banner" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    {{ successMessage }}
  </div>
  <div class="save-banner error" *ngIf="errorMessage">
    <i class="material-icons">error</i>
    {{ errorMessage }}
  </div>

  <div class="vehicle-form-container" *ngIf="showForm">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Driver' : 'Create Driver' }}</h2>
      <button class="btn-close" type="button" (click)="showForm = false">×</button>
    </div>

    <form [formGroup]="driverForm" (ngSubmit)="saveDriver()" class="vehicle-form">
      <div class="form-row">
        <div class="form-group">
          <label for="userFirstName">First name *</label>
          <input id="userFirstName" type="text" formControlName="userFirstName" class="form-control" placeholder="Thabo">
          <span class="error" *ngIf="isInvalid('userFirstName')">First name is required.</span>
        </div>
        <div class="form-group">
          <label for="userLastName">Last name</label>
          <input id="userLastName" type="text" formControlName="userLastName" class="form-control" placeholder="Mokoena">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="userPrefix">Prefix</label>
          <input id="userPrefix" type="text" formControlName="userPrefix" class="form-control" placeholder="Mr">
        </div>
        <div class="form-group">
          <label for="username">Username</label>
          <input id="username" type="text" formControlName="username" class="form-control" placeholder="thabo.mokoena">
          <span class="error" *ngIf="usernameError">{{ usernameError }}</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="userPassword">Password</label>
          <input id="userPassword" type="password" formControlName="userPassword" class="form-control" placeholder="Optional password">
          <small class="text-muted">Leave blank to keep the existing password when editing.</small>
        </div>
        <div class="form-group">
          <label for="statusID">Status *</label>
          <select id="statusID" formControlName="statusID" class="form-control">
            <option *ngFor="let status of statuses" [ngValue]="status.statusID">{{ status.statusName }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group" *ngIf="isAdminOrPlatform">
          <label for="organisationID">Organisation *</label>
          <select id="organisationID" formControlName="organisationID" class="form-control">
            <option [ngValue]="null">Select organisation</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
          <span class="error" *ngIf="isInvalid('organisationID')">Organisation is required.</span>
        </div>
        <div class="form-group">
          <label for="passengerProfileID">Passenger profile ID</label>
          <input id="passengerProfileID" type="number" formControlName="passengerProfileID" class="form-control" min="1" placeholder="Optional hotspot profile link">
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="showForm = false">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="isSaving">
          {{ isSaving ? 'Saving...' : 'Save Driver' }}
        </button>
      </div>
    </form>
  </div>

  <div class="route-workspace">
    <section class="routes-list">
      <div class="list-header">
        <div>
          <h3>Driver List ({{ drivers.length }})</h3>
          <p class="text-muted">{{ includeInactive ? 'Active and inactive drivers' : 'Active drivers only' }}</p>
        </div>
        <div class="filter-controls">
          <input class="form-control filter-select" type="search" placeholder="Search drivers" [(ngModel)]="searchTerm" (keyup.enter)="loadDrivers()">
          <select class="form-control filter-select" *ngIf="isAdminOrPlatform" [(ngModel)]="selectedOrganisationId" (ngModelChange)="loadDrivers()">
            <option [ngValue]="null">All organisations</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
          <label class="toggle-control">
            <input type="checkbox" [(ngModel)]="includeInactive" (ngModelChange)="loadDrivers()">
            <span>Include inactive</span>
          </label>
          <button class="btn btn-secondary" type="button" (click)="loadDrivers()">
            <i class="material-icons">search</i>
            Search
          </button>
        </div>
      </div>

      <div class="routes-table" *ngIf="drivers.length">
        <table>
          <thead>
            <tr>
              <th>Driver</th>
              <th *ngIf="isAdminOrPlatform">Organisation</th>
              <th>Status</th>
              <th>Hotspot profile</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let driver of drivers" [class.inactive-row]="isInactive(driver)" [class.selected-row]="selectedDriver?.userID === driver.userID" (click)="selectDriver(driver)">
              <td>
                <strong>{{ getDriverName(driver) }}</strong>
                <p class="text-muted">{{ driver.username || 'No username set' }}</p>
              </td>
              <td *ngIf="isAdminOrPlatform">{{ driver.organisationName || driver.organisationID }}</td>
              <td>
                <span class="badge" [class.badge-success]="!isInactive(driver)">{{ driver.statusName || (isInactive(driver) ? 'Inactive' : 'Active') }}</span>
                <small class="inactive-note" *ngIf="isInactive(driver)">Not available for new trip assignment</small>
              </td>
              <td>
                <span *ngIf="hasPassengerProfile(driver)">{{ driver.passengerDisplayName || ('Profile ' + driver.passengerProfileID) }}</span>
                <span class="text-muted" *ngIf="!hasPassengerProfile(driver)">Unlinked</span>
              </td>
              <td class="actions-cell" (click)="$event.stopPropagation()">
                <button class="btn-icon" type="button" title="View driver" (click)="selectDriver(driver)">
                  <i class="material-icons">visibility</i>
                </button>
                <button class="btn-icon" type="button" title="Edit driver" (click)="startEdit(driver)">
                  <i class="material-icons">edit</i>
                </button>
                <button class="btn-icon btn-danger" type="button" title="Deactivate driver" [disabled]="isInactive(driver)" (click)="deactivateDriver(driver)">
                  <i class="material-icons">person_off</i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="empty-state" *ngIf="!drivers.length && !isLoading">
        <i class="material-icons">person_search</i>
        <h3>No drivers found</h3>
        <p>Create a driver before assigning responsibility for trips.</p>
        <button class="btn btn-primary" type="button" (click)="startCreate()">Create Driver</button>
      </div>
    </section>

    <aside class="route-detail-panel" *ngIf="selectedDriver">
      <div class="list-header">
        <div>
          <h3>{{ getDriverName(selectedDriver) }}</h3>
          <p class="text-muted">{{ selectedDriver.username || 'No username set' }}</p>
        </div>
        <div class="header-actions">
          <span class="badge" [class.badge-success]="!isInactive(selectedDriver)">
            {{ selectedDriver.statusName || (isInactive(selectedDriver) ? 'Inactive' : 'Active') }}
          </span>
          <button class="btn btn-secondary" type="button" (click)="startEdit(selectedDriver)">
            <i class="material-icons">edit</i>
            Edit
          </button>
        </div>
      </div>

      <div class="inactive-callout" *ngIf="isInactive(selectedDriver)">
        <i class="material-icons">info</i>
        This driver is inactive and should not be assigned to new trips.
      </div>

      <div class="vehicle-details driver-detail-body">
        <div class="detail-item">
          <span class="label">Organisation</span>
          <span class="value">{{ selectedDriver.organisationName || selectedDriver.organisationID }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Created</span>
          <span class="value">{{ selectedDriver.userCreateDate || '-' }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Modified</span>
          <span class="value">{{ selectedDriver.userModifyDate || '-' }}</span>
        </div>
      </div>

      <section class="driver-profile-link">
        <div class="section-header">
          <h3>Hotspot passenger profile</h3>
        </div>
        <div class="linked-profile" *ngIf="hasPassengerProfile(selectedDriver); else noProfileLinked">
          <strong>{{ selectedDriver.passengerDisplayName || ('Profile ' + selectedDriver.passengerProfileID) }}</strong>
          <span>{{ selectedDriver.passengerPhone || 'No phone' }}</span>
          <span>{{ selectedDriver.passengerEmail || 'No email' }}</span>
        </div>
        <ng-template #noProfileLinked>
          <p class="text-muted">Link this driver to a hotspot passenger profile when the same person appears in customer data.</p>
        </ng-template>

        <div class="form-row compact-row">
          <div class="form-group">
            <label for="linkPassengerProfileId">Passenger profile ID</label>
            <input id="linkPassengerProfileId" type="number" min="1" class="form-control" [(ngModel)]="linkPassengerProfileId">
            <span class="error" *ngIf="linkError">{{ linkError }}</span>
          </div>
          <div class="form-actions align-end">
            <button class="btn btn-primary" type="button" (click)="linkPassengerProfile()">
              <i class="material-icons">link</i>
              Link Profile
            </button>
          </div>
        </div>
      </section>
    </aside>
  </div>
</div>
